/**
 * Business owner portal routes (unified authentication). Owner: business portal.
 * All render under PortalLayout (no site chrome). The public business page
 * /business/:slug lives in routes/public.jsx.
 *
 * A function, not a constant, so flag() is read on every render (runtime flags from
 * GET /api/flags can switch a route on).
 *
 * SLOT_MODEL (Phase 7b) swaps /business/onboarding to OnboardingSlot (same URL; it runs
 * its own sign-in gate, so it is NOT behind RequireBusiness). SLOT_MODEL + SLOT_BILLING add
 * /business/billing (Slots & billing, also the Stripe Checkout return URL) behind
 * RequireBusiness — the same gate as its nav entry (DashboardNavLinks). Otherwise nothing
 * changes: OnboardingNew stays (SLOT_MODEL off) and /business/billing is not declared here
 * (it falls to /business/:slug in routes/public.jsx, as before). Static segments outrank
 * :params in React Router's ranking, so with the flag on /business/billing wins over
 * /business/:slug. `billing` is a reserved business slug on the backend.
 *
 * SLOT_MODEL (Phase 7c) swaps the owner's everyday pages to the rebuilt slot pages on the
 * same URLs, all still behind RequireBusiness: /business/dashboard (SlotDashboard),
 * /business/deals (SlotOffers, labelled "Offers"), /business/deals/new and
 * /business/deals/edit/:dealId (SlotOfferForm), /business/settings (SlotSettings). It also
 * adds /business/leads (SlotLeads) and /business/reports (SlotReports), flag on only: with
 * the flag off they are not declared here and fall to /business/:slug (the public business
 * page for the reserved slugs "leads" / "reports"), as before. Static segments win over
 * /business/:slug with the flag on, as for /business/billing.
 *
 * SLOT_MODEL (Phase 7d) adds the advertiser page editor, flag on only, behind
 * RequireBusiness: /business/pages (SlotPages: entitlement + page list),
 * /business/pages/:pageId (SlotPageEditor) and /business/pages/:pageId/preview
 * (AdvertiserPagePreview: the public renderer in preview mode, a bare page with its own
 * preview bar and h1, no PortalShell). With the flag off none is declared: /business/pages
 * falls to /business/:slug (the public page for the reserved slug "pages") and the two deeper
 * paths to the public catch-all (NotFound), as before. With the flag on the static "pages"
 * segment outranks /business/:slug and /business/:slug/:page (routes/public.jsx).
 *
 * SLOT_MODEL (Phase 8B) adds /business/coasters (SlotCoasters: artwork uploads, proofs and
 * deliveries per venue), flag on only, behind RequireBusiness. With the flag off it is not
 * declared and falls to /business/:slug (the public page for the reserved slug "coasters").
 *
 * SLOT_MODEL (Phase 12 cutover): the legacy Local/Multi pages /business/subscription and
 * /business/subscription-thank-you redirect (replace) to /business/billing when SLOT_BILLING
 * is on too, else to /business/dashboard. /business/subscription-manage stays: businesses
 * still paying for a legacy plan reach the Stripe portal there (linked from Business details,
 * SlotSettings, when the profile shows a live legacy subscription). Site redesign Phase 8
 * swaps it to ManageSubscriptionSlot (same calls, on the portal frame); ManageSubscription
 * stays for flag-off. All stay behind RequireBusiness, so a signed-out visitor is asked to
 * sign in first.
 */
import React from "react";
import { Route, Navigate } from "react-router-dom";
import { flag } from "../config";
import { RequireBusiness } from "../components/routing/guards";
// A portal page loaded on demand, with its own Suspense boundary and PageLoadError fallback
// (routes/lazyPortalPage.jsx, shared with routes/venue.jsx).
import lazyPortalPage from "./lazyPortalPage";
import NotFound from "../pages/NotFound";
import BusinessDashboard from "../pages/business/BusinessDashboard";
import SubscriptionPlans from "../pages/business/SubscriptionPlans";
import SubscriptionThankYou from "../pages/business/SubscriptionThankYou";
import ManageSubscription from "../pages/business/ManageSubscription";
import BusinessSettings from "../pages/business/BusinessSettings";
import BusinessDeals from "../pages/business/BusinessDeals";
import Onboarding from "../pages/business/OnboardingNew";
import DealForm from "../pages/business/DealForm";

// Slot-model portal pages (Phase 7b). OnboardingSlot pulls in components/portal/form
// (react-hook-form + zod), which must stay out of the main chunk.
const OnboardingSlot = lazyPortalPage("OnboardingSlot", () => import("../pages/business/slot/OnboardingSlot"));
const SlotBilling = lazyPortalPage("SlotBilling", () => import("../pages/business/slot/SlotBilling"));
// Phase 7c. SlotOfferForm, SlotSettings and SlotLeads (through leadsParts) pull in zod and
// components/portal/form, so they must stay lazy too; the others are lazy for chunk size.
const SlotDashboard = lazyPortalPage("SlotDashboard", () => import("../pages/business/slot/SlotDashboard"));
const SlotOffers = lazyPortalPage("SlotOffers", () => import("../pages/business/slot/SlotOffers"));
const SlotOfferForm = lazyPortalPage("SlotOfferForm", () => import("../pages/business/slot/SlotOfferForm"));
const SlotLeads = lazyPortalPage("SlotLeads", () => import("../pages/business/slot/SlotLeads"));
const SlotReports = lazyPortalPage("SlotReports", () => import("../pages/business/slot/SlotReports"));
const SlotSettings = lazyPortalPage("SlotSettings", () => import("../pages/business/slot/SlotSettings"));
// Phase 7d. SlotPages and SlotPageEditor pull in zod, react-hook-form and components/portal/form;
// AdvertiserPagePreview (pages/public, a portal route) brings the public page renderers.
const SlotPages = lazyPortalPage("SlotPages", () => import("../pages/business/slot/SlotPages"));
const SlotPageEditor = lazyPortalPage("SlotPageEditor", () => import("../pages/business/slot/SlotPageEditor"));
const AdvertiserPagePreview = lazyPortalPage("AdvertiserPagePreview", () => import("../pages/public/AdvertiserPagePreview"));
// Phase 8B: coaster artwork, proofs and deliveries per venue (no zod; lazy for chunk size).
const SlotCoasters = lazyPortalPage("SlotCoasters", () => import("../pages/business/slot/SlotCoasters"));
// Site redesign Phase 8: the plan from before slots (legacy payers; lazy for chunk size).
const ManageSubscriptionSlot = lazyPortalPage("ManageSubscriptionSlot", () => import("../pages/business/slot/ManageSubscriptionSlot"));

export function businessRoutes() {
  const slot = flag("SLOT_MODEL");
  const slotBilling = slot && flag("SLOT_BILLING");
  // Phase 12: with SLOT_MODEL on the legacy Local/Multi plan pages redirect (their checkout
  // answers 410 on the backend): to Slots & billing when SLOT_BILLING is on too, else to the
  // dashboard. The manage page is kept for legacy payers (Stripe portal; ManageSubscriptionSlot
  // with the flag on since site redesign Phase 8). Flag off: the legacy pages, unchanged.
  const legacyBilling = slot
    ? <Navigate to={slotBilling ? "/business/billing" : "/business/dashboard"} replace />
    : null;
  return (
    <>
      <Route path="/business/onboarding" element={slot ? <OnboardingSlot /> : <Onboarding />} />
      <Route element={<RequireBusiness />}>
        <Route path="/business/dashboard" element={slot ? <SlotDashboard /> : <BusinessDashboard />} />
        <Route path="/business/subscription" element={legacyBilling || <SubscriptionPlans />} />
        <Route path="/business/subscription-manage" element={slot ? <ManageSubscriptionSlot /> : <ManageSubscription />} />
        <Route path="/business/settings" element={slot ? <SlotSettings /> : <BusinessSettings />} />
        <Route path="/business/subscription-thank-you" element={legacyBilling || <SubscriptionThankYou />} />
        <Route path="/business/deals" element={slot ? <SlotOffers /> : <BusinessDeals />} />
        <Route path="/business/deals/new" element={slot ? <SlotOfferForm /> : <DealForm />} />
        <Route path="/business/deals/edit/:dealId" element={slot ? <SlotOfferForm /> : <DealForm />} />
        {slotBilling && <Route path="/business/billing" element={<SlotBilling />} />}
        {slot && <Route path="/business/leads" element={<SlotLeads />} />}
        {slot && <Route path="/business/reports" element={<SlotReports />} />}
        {slot && <Route path="/business/pages" element={<SlotPages />} />}
        {slot && <Route path="/business/pages/:pageId" element={<SlotPageEditor />} />}
        {slot && <Route path="/business/pages/:pageId/preview" element={<AdvertiserPagePreview />} />}
        {slot && <Route path="/business/coasters" element={<SlotCoasters />} />}
      </Route>
      {/* Unknown deal-manager sub-paths: NotFound without site chrome, as before. */}
      <Route path="/business/deals/*" element={<NotFound />} />
    </>
  );
}
